import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserAvatar } from "@/components/common/UserAvatar";
import { AgencySummaryPrefs } from "@/components/agency/AgencySummaryPrefs";
import { DirectoryVisibility } from "@/pages/people/Directory";
import {
  User, Phone, Briefcase, PenLine, Plug, Link2, ShieldCheck, Mail, Calendar,
  CheckCircle2, AlertTriangle, RefreshCw, Plus, X, Bell,
} from "lucide-react";
import { toast } from "sonner";
import { SupportStatusControl } from "@/components/support/SupportStatusControl";
import { EmailSyncCard } from "@/components/comms/EmailSyncPanel";
import { TimezoneSetting } from "@/components/profile/TimezoneSetting";

const TABS = [
  ["personal", "Personal", User],
  ["contact", "Contact", Phone],
  ["work", "Work & Recruiting", Briefcase],
  ["signature", "Email Signature", PenLine],
  ["connected", "Connected Accounts", Plug],
  ["links", "Scheduling & Links", Link2],
  ["access", "My Access", ShieldCheck],
  ["preferences", "Preferences", Bell],
];

function Field({ label, value, onChange, placeholder, readOnly, testId, type = "text" }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <Input type={type} value={value || ""} onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        placeholder={placeholder} readOnly={readOnly} data-testid={testId}
        className={readOnly ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""} />
    </div>
  );
}

function Card({ title, description, children, footer }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="px-5 pt-5">
        <h3 className="font-semibold text-slate-800">{title}</h3>
        {description && <p className="text-sm text-slate-400 mt-0.5">{description}</p>}
      </div>
      <div className="px-5 py-4 space-y-4">{children}</div>
      {footer && <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">{footer}</div>}
    </div>
  );
}

function MySchedulingCard({ navigate }) {
  const [pages, setPages] = useState(null);
  useEffect(() => { api.get("/scheduling-pages/share-info").then((r) => setPages(r.data.items)).catch(() => setPages([])); }, []);
  if (pages === null) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm mb-4" data-testid="my-scheduling-card">
      <div className="px-5 pt-5 flex items-center justify-between">
        <div><h3 className="font-semibold text-slate-800">My Scheduling Page</h3><p className="text-sm text-slate-400 mt-0.5">Your public booking page. Manage it under Calendar → Scheduling Pages.</p></div>
        <Calendar className="w-5 h-5 text-brand" />
      </div>
      <div className="px-5 py-4 space-y-3">
        {pages.length === 0 ? (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">No active scheduling page yet.</p>
            <Button size="sm" variant="outline" onClick={() => navigate("/calendar/scheduling-pages")} data-testid="my-sched-create">Create one</Button>
          </div>
        ) : pages.map((p) => (
          <div key={p.id} className="rounded-lg border border-slate-200 p-3" data-testid={`my-sched-${p.id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Active</span>
            </div>
            <p className="text-xs text-slate-400 truncate mt-1">{p.public_url}</p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(p.public_url); toast.success("Scheduling link copied"); }} data-testid={`my-sched-copy-${p.id}`}>Copy Link</Button>
              <Button size="sm" variant="outline" onClick={() => window.open(p.public_url, "_blank")} data-testid={`my-sched-preview-${p.id}`}>Preview</Button>
              <Button size="sm" variant="outline" onClick={() => navigate(`/calendar/scheduling-pages/${p.id}`)} data-testid={`my-sched-edit-${p.id}`}>Edit</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MyProfile() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(params.get("tab") || "personal");
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/my-profile").then((r) => setData(r.data)).catch(() => toast.error("Could not load profile"));
  useEffect(() => { load(); }, []);

  const setSection = (section, patch) =>
    setData((d) => ({ ...d, [section]: { ...d[section], ...patch } }));

  const save = async (payload, msg = "Profile updated") => {
    setSaving(true);
    try { const { data: fresh } = await api.patch("/my-profile", payload); setData(fresh); toast.success(msg); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const squareCrop = (file) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const s = Math.min(img.width, img.height);
      const size = Math.min(s, 512);
      const c = document.createElement("canvas"); c.width = size; c.height = size;
      c.getContext("2d").drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, size, size);
      c.toBlob((blob) => resolve(blob || file), "image/jpeg", 0.9);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const blob = await squareCrop(file);
    const fd = new FormData(); fd.append("file", blob, "profile.jpg");
    try { await api.post("/my-profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Photo updated"); load(); }
    catch (err) { toast.error(apiError(err)); } finally { e.target.value = ""; }
  };
  const removePhoto = async () => {
    try { await api.delete("/my-profile/photo"); toast.success("Photo removed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!data) return null;

  return (
    <div className="max-w-3xl">
      <PageHeader title="My Profile"
        description="Manage your personal information, contact details, email signature, Microsoft connection and professional links." />

      <div className="flex items-center gap-4 mb-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        {data.personal.photo_url
          ? <img src={`${process.env.REACT_APP_BACKEND_URL}${data.personal.photo_url}`} alt="" className="w-14 h-14 rounded-full object-cover" data-testid="profile-photo" />
          : <UserAvatar name={data.personal.display_name || user?.name} size={56} />}
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-800 truncate" data-testid="profile-name">{data.personal.display_name || user?.name}</p>
          <p className="text-sm text-slate-400 truncate">{data.contact.primary_email}{data.work.title ? ` · ${data.work.title}` : ""}</p>
          <div className="flex gap-3 mt-1.5">
            <label className="text-xs font-medium text-brand hover:underline cursor-pointer" data-testid="profile-photo-upload">
              {data.personal.photo_url ? "Change photo" : "Upload photo"}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadPhoto} />
            </label>
            {data.personal.photo_url && <button type="button" className="text-xs text-slate-400 hover:text-rose-500" data-testid="profile-photo-remove" onClick={removePhoto}>Remove</button>}
          </div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-5 overflow-x-auto">
        {TABS.map(([k, l, I]) => (
          <button key={k} data-testid={`profile-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === k ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <I className="w-4 h-4" />{l}
          </button>
        ))}
      </div>

      {tab === "personal" && (
        <Card title="Personal Information" description="How your name appears across Top-Recruit."
          footer={<Button onClick={() => save({ ...data.personal }, "Personal info saved")} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="save-personal">Save Changes</Button>}>
          <Field label="Preferred / Display Name" value={data.personal.display_name} onChange={(v) => setSection("personal", { display_name: v })} testId="pf-display-name" placeholder="e.g. Jeff Hastings" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="First Name" value={data.personal.first_name} onChange={(v) => setSection("personal", { first_name: v })} testId="pf-first-name" />
            <Field label="Middle Name" value={data.personal.middle_name} onChange={(v) => setSection("personal", { middle_name: v })} testId="pf-middle-name" />
            <Field label="Last Name" value={data.personal.last_name} onChange={(v) => setSection("personal", { last_name: v })} testId="pf-last-name" />
          </div>
          <Field label="Credentials" value={data.personal.credentials} onChange={(v) => setSection("personal", { credentials: v })} testId="pf-credentials" placeholder="e.g. CLU, ChFC" />
        </Card>
      )}

      {tab === "contact" && (
        <Card title="Contact Information" description="Your primary email is your login and is managed under Security & MFA."
          footer={<Button onClick={() => save({ ...data.contact }, "Contact info saved")} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="save-contact">Save Changes</Button>}>
          <Field label="Primary Email (login)" value={data.contact.primary_email} readOnly testId="pf-primary-email" />
          <AltEmails value={data.contact.alt_emails} onChange={(v) => setSection("contact", { alt_emails: v })} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Mobile Phone" value={data.contact.mobile_phone} onChange={(v) => setSection("contact", { mobile_phone: v })} testId="pf-mobile" />
            <Field label="Office / Direct Phone" value={data.contact.office_phone} onChange={(v) => setSection("contact", { office_phone: v })} testId="pf-office-phone" />
            <Field label="Office Main Phone" value={data.contact.office_main_phone} onChange={(v) => setSection("contact", { office_main_phone: v })} testId="pf-office-main" />
          </div>
          <Field label="Address" value={data.contact.address_line} onChange={(v) => setSection("contact", { address_line: v })} testId="pf-address" placeholder="Street address" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="City" value={data.contact.city} onChange={(v) => setSection("contact", { city: v })} testId="pf-city" />
            <Field label="State" value={data.contact.state} onChange={(v) => setSection("contact", { state: v })} testId="pf-state" />
            <Field label="Postal Code" value={data.contact.postal_code} onChange={(v) => setSection("contact", { postal_code: v })} testId="pf-postal" />
          </div>
        </Card>
      )}

      {tab === "work" && (
        <Card title="Work & Recruiting Profile" description="Used in your email signature and merge fields."
          footer={<Button onClick={() => save({ ...data.work }, "Work profile saved")} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="save-work">Save Changes</Button>}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Job Title" value={data.work.title} onChange={(v) => setSection("work", { title: v })} testId="pf-title" />
            <Field label="Company / Team" value={data.work.company_or_team} onChange={(v) => setSection("work", { company_or_team: v })} testId="pf-company" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Manager / Group" value={data.work.manager_name} onChange={(v) => setSection("work", { manager_name: v })} testId="pf-manager" />
            <Field label="Markets / Regions" value={data.work.markets} onChange={(v) => setSection("work", { markets: v })} testId="pf-markets" />
          </div>
          <Field label="Office Address" value={data.work.office_address} onChange={(v) => setSection("work", { office_address: v })} testId="pf-office-address" />
          <Field label="Recruiting Direct Phone" value={data.work.recruiting_direct_phone} onChange={(v) => setSection("work", { recruiting_direct_phone: v })} testId="pf-recruiting-phone" />
          <div className="pt-2"><SupportStatusControl variant="profile" /></div>
          <div className="pt-2"><TimezoneSetting /></div>
        </Card>
      )}

      {tab === "signature" && <SignatureSection data={data} setSection={setSection} save={save} saving={saving} />}
      {tab === "connected" && <ConnectedAccounts params={params} navigate={navigate} />}

      {tab === "links" && (
        <>
        <MySchedulingCard navigate={navigate} />
        <Card title="Scheduling & Professional Links" description="Add your scheduling and professional profile links."
          footer={<Button onClick={() => save({ ...data.links }, "Links saved")} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="save-links">Save Changes</Button>}>
          <Field label="Calendar / Scheduling URL" value={data.links.calendar_link} onChange={(v) => setSection("links", { calendar_link: v })} testId="pf-calendar-link" placeholder="https://…" />
          <Field label="LinkedIn URL" value={data.links.linkedin_url} onChange={(v) => setSection("links", { linkedin_url: v })} testId="pf-linkedin" placeholder="https://www.linkedin.com/in/…" />
          <OtherLinks value={data.links.other_links} onChange={(v) => setSection("links", { other_links: v })} />
        </Card>
        </>
      )}

      {tab === "access" && <AccessSection access={data.access} onGoSecurity={() => navigate("/security")} />}
      {tab === "preferences" && <div className="mb-4 max-w-md"><DirectoryVisibility /></div>}
      {tab === "preferences" && <PreferencesSection data={data} setSection={setSection} save={save} saving={saving} />}
    </div>
  );
}

const WEEKDAYS = [["0", "Monday"], ["1", "Tuesday"], ["2", "Wednesday"], ["3", "Thursday"], ["4", "Friday"], ["5", "Saturday"], ["6", "Sunday"]];
function PreferencesSection(props) {
  const { isOfficeUser } = useAuth();
  return isOfficeUser ? <AgencySummaryPrefs /> : <RecruitingPreferences {...props} />;
}

function RecruitingPreferences({ data, setSection, save, saving }) {
  const { isManager, isAdmin, isSuper } = useAuth();
  const canTeam = isManager || isAdmin || isSuper;
  const p = data.preferences || { digest_enabled: true, digest_weekday: 0, digest_hour: 7 };
  const hour12 = (h) => `${((h % 12) || 12)}:00 ${h < 12 ? "AM" : "PM"}`;
  const [preview, setPreview] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [testing, setTesting] = useState(false);
  const openPreview = async () => {
    setLoadingPrev(true);
    try { const { data: d } = await api.get("/my-profile/digest-preview"); setPreview(d.blocks || []); }
    catch (e) { toast.error(apiError(e)); } finally { setLoadingPrev(false); }
  };
  const sendTest = async () => {
    setTesting(true);
    try {
      const { data: d } = await api.post("/my-profile/digest-test");
      if (d.sent) toast.success(`Test digest sent to ${d.to}`);
      else toast.message(d.message || "Nothing to send right now.");
    } catch (e) { toast.error(apiError(e)); } finally { setTesting(false); }
  };
  const savePrefs = () => save({
    digest_enabled: p.digest_enabled, digest_weekday: Number(p.digest_weekday), digest_hour: Number(p.digest_hour),
    digest_include_interviews: p.digest_include_interviews !== false,
    digest_include_attention: p.digest_include_attention !== false,
    digest_include_ready: p.digest_include_ready !== false,
    digest_team_only: !!p.digest_team_only,
  }, "Preferences saved");
  const CB = ({ k, label, testId }) => (
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={p[k] !== false} onChange={(e) => setSection("preferences", { [k]: e.target.checked })} data-testid={testId} />{label}
    </label>
  );
  return (
    <>
    <Card title="Email Preferences" description="Control your weekly recruiting digest (times are U.S. Central)."
      footer={<Button onClick={savePrefs} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="save-preferences">Save Changes</Button>}>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={!!p.digest_enabled} onChange={(e) => setSection("preferences", { digest_enabled: e.target.checked })} data-testid="pref-digest-toggle" />
        Send me a weekly recruiting digest
      </label>
      {p.digest_enabled && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Day</Label>
              <select className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm" value={String(p.digest_weekday)} onChange={(e) => setSection("preferences", { digest_weekday: e.target.value })} data-testid="pref-digest-day">
                {WEEKDAYS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Time</Label>
              <select className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm" value={String(p.digest_hour)} onChange={(e) => setSection("preferences", { digest_hour: e.target.value })} data-testid="pref-digest-hour">
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hour12(h)}</option>)}
              </select>
            </div>
          </div>
          <div className="pt-1">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Include in my digest</p>
            <div className="space-y-1.5">
              <CB k="digest_include_interviews" label="Interviews this week" testId="pref-include-interviews" />
              <CB k="digest_include_attention" label="Candidates needing attention" testId="pref-include-attention" />
              <CB k="digest_include_ready" label="Ready for review" testId="pref-include-ready" />
            </div>
          </div>
          {canTeam && (
            <label className="flex items-center gap-2 text-sm text-slate-700 pt-1 border-t border-slate-100 mt-1">
              <input type="checkbox" checked={!!p.digest_team_only} onChange={(e) => setSection("preferences", { digest_team_only: e.target.checked })} data-testid="pref-team-only" />
              Team summary only (skip my personal digest)
            </label>
          )}
          <div className="pt-3 border-t border-slate-100 mt-1">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Preview &amp; test</p>
            <p className="text-xs text-slate-400 mb-2">See exactly what your next digest will contain, or send yourself a live test copy (marked TEST).</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={openPreview} disabled={loadingPrev} data-testid="digest-preview-btn">{loadingPrev ? "Loading…" : "Preview My Digest"}</Button>
              <Button size="sm" className="bg-brand hover:bg-brand-strong" onClick={sendTest} disabled={testing} data-testid="digest-test-send-btn">{testing ? "Sending…" : "Send Test to Me"}</Button>
            </div>
          </div>
        </>
      )}
    </Card>
    {preview !== null && <DigestPreviewDialog blocks={preview} onClose={() => setPreview(null)} />}
    </>
  );
}

function DigestPreviewDialog({ blocks, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl" data-testid="digest-preview-dialog">
        <DialogHeader><DialogTitle>Weekly Digest Preview</DialogTitle></DialogHeader>
        <div className="rounded-md bg-amber-50 text-amber-800 text-xs font-medium px-3 py-2 border border-amber-200" data-testid="digest-preview-test-note">
          This is exactly what your digest would contain using your current settings. Use “Send Test to Me” to receive a live copy in your inbox (clearly marked TEST).
        </div>
        <div className="max-h-[60vh] overflow-y-auto space-y-4 mt-1">
          {blocks.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center" data-testid="digest-preview-empty">Your digest is empty right now — no active candidates, interviews or attention items to report.</p>
          ) : blocks.map((b, i) => (
            <div key={i} data-testid={`digest-preview-block-${i}`}>
              <p className="text-xs font-semibold text-slate-600 mb-1">Subject: {b.subject}</p>
              <iframe title={`digest-${i}`} srcDoc={b.html} sandbox="" className="w-full h-[420px] rounded-lg border border-slate-200 bg-white" />
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AltEmails({ value = [], onChange }) {
  const [draft, setDraft] = useState("");
  const add = () => { const v = draft.trim(); if (v) { onChange([...(value || []), v]); setDraft(""); } };
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-600">Alternate Email(s)</Label>
      <div className="flex flex-wrap gap-2 mb-2">
        {(value || []).map((e, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700" data-testid={`alt-email-${i}`}>
            {e}<button onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-500"><X className="w-3.5 h-3.5" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="add another email" data-testid="alt-email-input" />
        <Button type="button" variant="outline" onClick={add} data-testid="alt-email-add"><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function OtherLinks({ value = [], onChange }) {
  const [label, setLabel] = useState(""); const [url, setUrl] = useState("");
  const add = () => { if (label.trim() && url.trim()) { onChange([...(value || []), { label: label.trim(), url: url.trim() }]); setLabel(""); setUrl(""); } };
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-600">Other Professional Links</Label>
      <div className="space-y-2 mb-2">
        {(value || []).map((l, i) => (
          <div key={i} className="flex items-center gap-2 text-sm" data-testid={`other-link-${i}`}>
            <span className="font-medium text-slate-700">{l.label}</span>
            <a href={l.url} className="text-brand truncate" target="_blank" rel="noreferrer">{l.url}</a>
            <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="ml-auto text-slate-400 hover:text-rose-500"><X className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label" className="w-1/3" data-testid="other-link-label" />
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" data-testid="other-link-url" />
        <Button type="button" variant="outline" onClick={add} data-testid="other-link-add"><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function SignatureSection({ data, setSection, save, saving }) {
  const [mode, setMode] = useState("design");
  const sig = data.signature;
  return (
    <Card title="Email Signature" description="This signature is appended to the recruiting email you send from Communications → Compose."
      footer={<Button onClick={() => save({ html: sig.html, include_promo: sig.include_promo, promo_html: sig.promo_html, logo_url: sig.logo_url }, "Signature saved")} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="save-signature">Save Signature</Button>}>
      <div className="flex gap-1">
        {[["design", "Design"], ["html", "HTML"], ["preview", "Preview"]].map(([m, l]) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`sig-mode-${m}`}
            className={`text-xs px-3 py-1 rounded-full border ${mode === m ? "bg-brand text-white border-brand" : "border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>
      {mode === "preview" ? (
        <div className="border rounded-lg p-4 min-h-[140px] bg-white" data-testid="sig-preview" dangerouslySetInnerHTML={{ __html: sig.html || "<em class='text-slate-400'>No signature yet</em>" }} />
      ) : (
        <Textarea rows={8} value={sig.html || ""} onChange={(e) => setSection("signature", { html: e.target.value })} data-testid="sig-input"
          className={mode === "html" ? "font-mono text-xs" : ""}
          placeholder={"Jeff Hastings, CLU\nRecruiting Director\nTop-Recruit\n(555) 123-4567"} />
      )}
      <Field label="Logo URL (optional)" value={sig.logo_url} onChange={(v) => setSection("signature", { logo_url: v })} testId="sig-logo" placeholder="https://… (image URL)" />
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={!!sig.include_promo} onChange={(e) => setSection("signature", { include_promo: e.target.checked })} data-testid="sig-promo-toggle" />
        Include an optional promotional block
      </label>
      {sig.include_promo && (
        <Textarea rows={4} value={sig.promo_html || ""} onChange={(e) => setSection("signature", { promo_html: e.target.value })} data-testid="sig-promo-input" placeholder="Optional promotional message or link" />
      )}
    </Card>
  );
}

function ConnectedAccounts({ params, navigate }) {
  const { user } = useAuth();
  const [conn, setConn] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/comm-connection").then((r) => setConn(r.data)).catch(() => setConn({ connected: false }));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (params.get("connected")) { toast.success("Microsoft 365 connected"); navigate("/profile?tab=connected", { replace: true }); }
    else if (params.get("error")) { toast.error(params.get("error")); navigate("/profile?tab=connected", { replace: true }); }
  }, [params, navigate]);

  const connect = async () => {
    setBusy(true);
    try { const { data } = await api.post("/comm-connection/microsoft/connect"); if (data.authorize_url) window.location.href = data.authorize_url; else toast.error("Could not start Microsoft sign-in."); }
    catch (e) { toast.error(apiError(e)); setBusy(false); }
  };
  const disconnect = async () => {
    try { await api.delete("/comm-connection"); toast.success("Microsoft 365 disconnected"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  if (!conn) return null;
  const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

  return (
    <Card title="Connected Accounts" description="Connect your Microsoft 365 mailbox to send recruiting email from your own address.">
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm" data-testid="connected-accounts">
        <p className="text-slate-600">Top-Recruit Login: <b className="text-slate-800" data-testid="login-email">{conn.login_email || user?.email || "—"}</b></p>
        <p className="text-slate-600 mt-0.5">Microsoft 365: <b className="text-slate-800" data-testid="ms-connected-as">{conn.connected ? conn.email : "Not Connected"}</b></p>
      </div>

      <div className="rounded-lg border border-slate-200 p-4" data-testid="connection-card">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="w-5 h-5 text-[#006090]" /><Calendar className="w-5 h-5 text-[#006090]" />
          <h4 className="font-semibold text-slate-800">Microsoft 365 / Outlook</h4>
          {conn.connected
            ? <span className="ml-auto text-xs bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 flex items-center gap-1" data-testid="ms-status-connected"><CheckCircle2 className="w-3 h-3" />Connected</span>
            : <span className="ml-auto text-xs bg-slate-100 text-slate-500 rounded-full px-2 py-0.5" data-testid="ms-status-disconnected">Not connected</span>}
        </div>

        {conn.login_email_restricted && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="farmers-notice">
            <p className="font-semibold">FARMERS MICROSOFT ACCOUNT NOTICE</p>
            <p className="mt-1">{conn.restriction_message}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              <Button size="sm" className="bg-[#006090] hover:bg-[#004E75]" onClick={connect} disabled={busy} data-testid="connect-different-ms">Connect a Different Microsoft Account</Button>
              {!conn.connected && <Button size="sm" variant="outline" onClick={() => toast.message("No problem — you can connect Microsoft later from this page.")} data-testid="continue-without-ms">Continue Without Microsoft Connection</Button>}
            </div>
          </div>
        )}

        {conn.needs_reconnect && (
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="ms-reconnect-banner">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Your Microsoft connection needs to be renewed. Please reconnect to keep sending email and syncing your calendar.</span>
          </div>
        )}

        {conn.connected ? (
          <>
            <p className="text-sm text-slate-700" data-testid="ms-display-name">Account: <b>{conn.display_name || "—"}</b></p>
            <p className="text-sm text-slate-600" data-testid="ms-email">Mailbox: <b>{conn.email}</b></p>
            <p className="text-xs text-slate-400 mt-0.5">Connected: {fmt(conn.connected_at)} · Last sync: {fmt(conn.last_sync)}</p>
            <p className="text-xs text-slate-500 mt-1.5" data-testid="ms-calendar-note">Interviews you schedule sync to your Outlook calendar. If they don't appear, use Reconnect to grant calendar access.</p>
            {!conn.large_attachments_ready && <p className="text-xs text-amber-700 mt-1" data-testid="ms-large-attach-note">To send attachments over 3 MB (such as full program guides), click Reconnect once and approve the extra Microsoft permission.</p>}
            <div className="flex gap-2 mt-3">
              <Button variant="outline" onClick={connect} disabled={busy} data-testid="reconnect-btn"><RefreshCw className="w-4 h-4 mr-1.5" />Reconnect</Button>
              <Button variant="outline" className="text-rose-600" onClick={disconnect} data-testid="disconnect-btn">Disconnect</Button>
            </div>
          </>
        ) : !conn.login_email_restricted && (
          <>
            <p className="text-sm text-slate-500 mb-3">Sign in with Microsoft to send email from your own mailbox. Top-Recruit never sees your password, and your tokens are stored securely on the server.</p>
            <Button className="bg-[#006090] hover:bg-[#004E75]" onClick={connect} disabled={busy} data-testid="connect-microsoft">
              <Plug className="w-4 h-4 mr-1.5" />{busy ? "Starting…" : conn.needs_reconnect ? "Reconnect Microsoft 365" : "Connect Microsoft 365"}
            </Button>
            {conn.configured === false && <p className="text-xs text-amber-600 mt-2">Microsoft 365 is not configured for this environment yet.</p>}
          </>
        )}
      </div>
      {conn.connected && <EmailSyncCard />}
    </Card>
  );
}

function AccessSection({ access, onGoSecurity }) {
  return (
    <Card title="My Access" description="These are set by your administrator and cannot be changed here.">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <div data-testid="access-role"><p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Role</p><p className="text-slate-800">{access.roles?.length ? access.roles.join(", ") : "—"}</p></div>
        <div data-testid="access-account"><p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Client Account</p><p className="text-slate-800">{access.account_name || (access.is_super ? "All accounts (Super Admin)" : "—")}</p></div>
        <div className="sm:col-span-2" data-testid="access-orgs"><p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Groups / Districts</p>
          {access.organizations?.length ? (
            <ul className="list-disc pl-5 text-slate-800">{access.organizations.map((o) => <li key={o.id}>{o.name}{o.org_code ? ` (${o.org_code})` : ""}</li>)}</ul>
          ) : <p className="text-slate-500">{access.is_super ? "All districts (Super Admin)" : "—"}</p>}
        </div>
      </div>
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-slate-400" />
        Password and multi-factor authentication are managed under
        <button onClick={onGoSecurity} className="text-brand font-medium underline ml-1" data-testid="access-go-security">Security &amp; MFA</button>.
      </div>
    </Card>
  );
}
